import { Component, OnInit, Input, ElementRef, EventEmitter, Output, forwardRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Observable, combineLatest, Subject, of } from 'rxjs';
import { startWith, map, switchMap, catchError } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { AsyncPipe, NgClass } from '@angular/common';

@Component({
  selector: 'app-multi-labels',
  templateUrl: './multi-labels.component.html',
  styleUrls: ['./multi-labels.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiLabelsComponent),
    multi: true
  }
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    AsyncPipe,
    NgClass
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiLabelsComponent implements OnInit, ControlValueAccessor {
  @Input()
  searchKey: string;

  @Input()
  readOnly: boolean = false;

  @Input()
  placeholder: string;

  @Input()
  labels: any[] = [];

  @Input()
  availableLabelsQuery: Observable<any[]>;

  @Input()
  optionBadgeFn: (item: any) => { text: string; cssClass: string } | null = null;

  @Output()
  changed = new EventEmitter<any[]>();


  readonly labelInput = viewChild<ElementRef<HTMLInputElement>>('labelInput');

  readonly matAutocomplete = viewChild<MatAutocomplete>('auto');

  readonly matAutocompleteTrigger = viewChild<MatAutocompleteTrigger>('trigger');

  @Input()
  refreshAutocomplete = new Subject<string>(); // used by parent to notify when the autocomplete datasource has changed

  @Input()
  isCountry: boolean = false;

  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  labelCtrl = new FormControl({ value: null, disabled: this.readOnly });
  filteredLabels: Observable<any[]>;
  labelList: any[] = [];

  constructor() {
  }

  ngOnInit() {
    this.filteredLabels = this.refreshAutocomplete
      .pipe(
        switchMap(() =>
          combineLatest([
            (this.availableLabelsQuery ?? of([])).pipe(catchError(() => of([]))),
            this.labelCtrl.valueChanges.pipe(startWith(null))
          ])
        ),
        map(([availableLabels, input]) => {
          return availableLabels.filter(label => {
            if (this.labels?.length && this.labels[0].hasOwnProperty('productId') && this.labels.some(l => l.productId === label.productId)) {
              return false;
            }

            if (!input) return true;

            if (this.searchKey) {

              if (input[this.searchKey]) {
                return this.isCountry ?
                  label[this.searchKey].toLowerCase().includes(input[this.searchKey].toLowerCase()) :
                  label[this.searchKey].toLowerCase().startsWith(input[this.searchKey].toLowerCase());
              }
              else return this.isCountry ?
                label[this.searchKey].toLowerCase().includes(input.toLowerCase()) :
                label[this.searchKey].toLowerCase().startsWith(input.toLowerCase());
            }

            if (this.isCountry)
              return label.toLowerCase().includes(input.toLowerCase())

            return label.toLowerCase().startsWith(input.toLowerCase())

          })
        }
        )
      );

    if (this.isCountry) {
      this.availableLabelsQuery.subscribe(list => {
        this.labelList = list
      })
    }
  }

  onOpened(_e: any) {
    this.forceRefreshAutocomplete();
  }

  forceRefreshAutocomplete() {
    // this.labelCtrl.setValue(null);
    this.refreshAutocomplete.next(null);
  }

  isArrayOfStrings(value: any): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
  }

  add(event: MatChipInputEvent): void {
    if (this.readOnly)
      return;

    // Add label only when MatAutocomplete is not open to make sure this does not conflict with OptionSelected Event
    if (this.matAutocomplete().isOpen)
      return;

    if (!this.labels)
      this.labels = [];

    const input = event.input;
    const value = event.value;

    // Add our label
    const label = (value || '').trim();
    if (label) {
      let alreadyAdded;
      if (this.isCountry) {
        alreadyAdded = this.labels.filter(x => x?.productId.trim().toLowerCase() === label.toLocaleLowerCase()).length > 0;
      } else if (!this.isArrayOfStrings(this.labels)) {
        alreadyAdded = this.labels.filter(x => x[this.searchKey].trim().toLowerCase() === label.toLocaleLowerCase()).length > 0;
      }
      else {
        alreadyAdded = this.labels.filter(x => x.trim().toLowerCase() === label.toLocaleLowerCase()).length > 0;
      }

      if (!alreadyAdded) {
        this.labels.push(value.trim());
        this.changed.emit(this.labels);
        this.propagateChange(this.labels);
      }
    }

    // Reset the input value
    if (input)
      input.value = '';

    if (this.isCountry) {
      this.labels = this.labels?.filter(label => this.labelList?.map(item => item?.productId).includes(label?.productId))
      this.changed.emit(this.labels);
      this.propagateChange(this.labels);
    }

    this.labelCtrl.setValue(null);
    this.labelInput().nativeElement.blur();
    this.matAutocompleteTrigger().closePanel();
  }

  remove(label: any): void {
    if (this.readOnly)
      return;

    const index = this.labels.indexOf(label);
    if (index >= 0) {
      if (this.isCountry) {
        this.labels = this.labels.filter(item => item?.productId != label?.productId)
      } else {
        this.labels.splice(index, 1);
      }

      this.changed.emit(this.labels);
      this.propagateChange(this.labels);
    }
    this.labelInput().nativeElement.blur();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.readOnly)
      return;

    if (!this.labels)
      this.labels = [];

    const label = event.option.value;
    const alreadyAdded = this.labels.filter(x => {
      if (this.searchKey) {
        return x[this.searchKey].trim().toLowerCase() === label[this.searchKey].toLocaleLowerCase()
      }
      return x.trim().toLowerCase() === label.toLocaleLowerCase();
    }).length > 0;
    if (!alreadyAdded) {
      this.labels.push(label);
      this.changed.emit(this.labels);
      this.propagateChange(this.labels);
    }

    this.labelInput().nativeElement.value = '';
    this.labelInput().nativeElement.blur();
    this.labelCtrl.setValue(null);
  }


  // ControlValueAccessor
  propagateChange = (_: any) => { };
  writeValue(obj: any): void {
    if (!obj) {
      this.labels = [];
    } else if (typeof obj === 'string') {
      this.labels = [obj];
    } else if (obj.length) {
      this.labels = obj
    } else {
      this.labels = [];
    }
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(_fn: any): void {
  }

  displayFn(selectedoption: any) {
    return selectedoption ? selectedoption[this.searchKey] : undefined;
  }

}
